/**
 * Per-zone draft pools — weighted species lists that rollOffers samples
 * at draft time. Replaces the fixed routeDrafts.ts pools so each draft
 * modal can surface a different combination on each run.
 *
 * The pool entry shape (species + weight + levelRange) keeps display
 * metadata (display name, types, dex id) OUT of the pool — those are
 * resolved at rollDraftOffer time via @pkmn/dex + SPECIES_TO_DEX. The
 * pool is the *contract for what could be drafted in a zone*; the
 * presentation of any single offer lives downstream.
 *
 * Anti-duplication is the caller's responsibility — rollOffers takes an
 * `excludedSpecies` set and filters before sampling. The intra-modal
 * dedup (no two offers in the same modal have the same species) is
 * built-in via remaining-list splicing.
 *
 * Section 3 of the Pokédex design doc spec.
 */

export interface DraftPoolEntry {
  /** @pkmn species id (lowercase, e.g. 'pidgey'). */
  species: string;
  /** Relative weight in the pool. Larger = more likely. Doesn't need to
   *  sum to a particular value across the pool. */
  weight: number;
  /** Inclusive level range for this species in this zone. rollDraftOffer
   *  picks one level uniformly from [min, max]. */
  levelRange: [number, number];
}

export interface DraftPool {
  /** Modal title — "Choose your starter" / "Route 1 draft". */
  title: string;
  /** How many offers to surface. The actual count may shrink if the
   *  available-after-dedup list is smaller (rare in v0.1). */
  pickCount: number;
  pool: DraftPoolEntry[];
}

/**
 * Per-zone pool table. Short region/map name keys (matches mapData.name in
 * interior mode and Region.name in mega-map exterior mode). Rekeyed from
 * the long `MAP_*` cartridge form as part of seamless-world Step 3 —
 * every per-map state set is now keyed by the short name. Starter draft
 * lives under Oak's Lab so it flows through the same rollOffers path —
 * its 3 starters get rolled IVs / nature / ability / possible egg pull
 * just like zone drafts (eng review Code Quality #3).
 */
export const DRAFT_POOLS: Record<string, DraftPool> = {
  PalletTown_ProfessorOaksLab: {
    title: 'Pick a starter',
    pickCount: 3,
    pool: [
      { species: 'bulbasaur', weight: 1, levelRange: [5, 5] },
      { species: 'charmander', weight: 1, levelRange: [5, 5] },
      { species: 'squirtle', weight: 1, levelRange: [5, 5] },
    ],
  },
  // Route 1 is the cartridge's tutorial grass — exactly Pidgey/Rattata,
  // 50/50 (wild_encounters.json MAP_ROUTE1). The draft mirrors that as a
  // clean 2-card either/or; the interesting variance starts on Route 2.
  Route1: {
    title: 'Route 1 draft',
    pickCount: 2,
    pool: [
      { species: 'pidgey', weight: 1, levelRange: [3, 5] },
      { species: 'rattata', weight: 1, levelRange: [3, 5] },
    ],
  },
  // Route 2 (both halves — below and above the Forest — share this zone
  // key) draws 3 of 5. Cartridge Route 2 is Pidgey/Rattata 45% each +
  // Caterpie/Weedle 5%; Spearow joins as the uncommon (it vacated the
  // Route 1 pool, and stays period-faithful — cartridge Route 22 is one
  // map over). One of Pidgey/Rattata is always excluded here (whichever
  // was picked on Route 1), so the effective roll is 3-of-4-or-5.
  Route2: {
    title: 'Route 2 draft',
    pickCount: 3,
    pool: [
      { species: 'pidgey', weight: 4, levelRange: [4, 6] },
      { species: 'rattata', weight: 4, levelRange: [4, 6] },
      { species: 'spearow', weight: 2, levelRange: [4, 6] },
      { species: 'caterpie', weight: 1, levelRange: [5, 6] },
      { species: 'weedle', weight: 1, levelRange: [5, 6] },
    ],
  },
  // Forest weights track the cartridge slot shares (Caterpie/Weedle 40%
  // each, Metapod/Kakuna ~10%, Pikachu 5%). Pikachu is the rare pull:
  // at weight 1 vs 10/10/4/4 the chance it appears among the 3 offered
  // cards is ~16% per run (weighted sampling without replacement) — a
  // find, not a fixture. It no longer appears in any other zone's pool.
  ViridianForest: {
    title: 'Viridian Forest draft',
    pickCount: 3,
    pool: [
      { species: 'caterpie', weight: 10, levelRange: [6, 8] },
      { species: 'weedle', weight: 10, levelRange: [6, 8] },
      { species: 'metapod', weight: 4, levelRange: [7, 9] },
      { species: 'kakuna', weight: 4, levelRange: [7, 9] },
      { species: 'pikachu', weight: 1, levelRange: [7, 9] },
    ],
  },
};

// Legacy `MAP_*` aliases for `encounter-determinism.test.ts`, the sacred
// seamless-world CI gate (handoff: byte-identical, no edits). The draft-
// determinism block in that file passes literal 'MAP_ROUTE1' to rollOffers,
// which reads DRAFT_POOLS internally. These aliases keep that lookup intact
// after the rekey. Delete once the gate test is updated.
const LEGACY_MAP_ALIASES: Record<string, string> = {
  MAP_PALLET_TOWN_PROFESSOR_OAKS_LAB: 'PalletTown_ProfessorOaksLab',
  MAP_ROUTE1: 'Route1',
  MAP_ROUTE2: 'Route2',
  MAP_VIRIDIAN_FOREST: 'ViridianForest',
};
for (const [legacy, short] of Object.entries(LEGACY_MAP_ALIASES)) {
  const pool = DRAFT_POOLS[short];
  if (pool) DRAFT_POOLS[legacy] = pool;
}

/**
 * Weighted pick from a list. Each item is selected with probability
 * `weight / Σweights`. Consumes exactly one rng() call regardless of
 * list size — load-bearing for the canonical draw order in Section 6
 * of the design doc.
 *
 * Linear-scan implementation (fine for v0.1 pool sizes ≤ ~10 entries).
 * Returns the last item as a tie-breaker fallback in the floating-point
 * edge case where the cumulative subtraction undershoots `roll`.
 */
export function weightedPick<T>(
  items: ReadonlyArray<T>,
  getWeight: (item: T) => number,
  rng: () => number,
): T {
  if (items.length === 0) {
    throw new Error('weightedPick: empty items');
  }
  const total = items.reduce((s, i) => s + getWeight(i), 0);
  let roll = rng() * total;
  for (const item of items) {
    roll -= getWeight(item);
    if (roll <= 0) return item;
  }
  return items[items.length - 1]!;
}
